import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';
import { 
  TrendingUp, TrendingDown, Minus, RefreshCw, Loader2, 
  BarChart3, Calendar, Shield, AlertCircle, Sparkles, Check
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

export default function ValuationHistory({ assetId, assetName, onValueAccepted }) {
  const { formatCurrency } = useCurrency();
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [valuating, setValuating] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await api.getValuationHistory(assetId);
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch valuation history:', error);
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleRequestValuation = async () => {
    setValuating(true);
    try {
      await api.valuateAsset(assetId);
      toast.success('AI valuation complete!');
      await fetchHistory();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('AI Valuation requires Professional or Elite membership');
      } else {
        toast.error('Failed to generate valuation');
      }
    } finally {
      setValuating(false);
    }
  };

  const handleAcceptValuation = async () => {
    setAccepting(true);
    try {
      const response = await api.acceptAIValuation(assetId);
      toast.success(`Asset value updated to ${formatCurrency(response.data.new_value)}`);
      
      // Refresh history to show updated values
      await fetchHistory();
      
      // Notify parent component if callback provided
      if (onValueAccepted) {
        onValueAccepted(response.data.new_value);
      }
    } catch (error) {
      toast.error('Failed to update asset value');
    } finally {
      setAccepting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return formatDate(dateString);
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp size={16} className="text-emerald-400" />;
      case 'down': return <TrendingDown size={16} className="text-red-400" />;
      default: return <Minus size={16} className="text-muted-foreground" />;
    }
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return 'text-emerald-400 bg-emerald-400/10';
      case 'medium': return 'text-yellow-400 bg-yellow-400/10';
      case 'low': return 'text-orange-400 bg-orange-400/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  if (loading) {
    return (
      <div className="luxury-card p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="luxury-card p-6" data-testid="valuation-history">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Sparkles size={20} className="text-gold" />
          <h3 className="font-serif text-lg text-white">AI Valuation History</h3>
        </div>
        <Button
          onClick={handleRequestValuation}
          disabled={valuating}
          className="btn-primary"
          data-testid="request-valuation-btn"
        >
          {valuating ? (
            <>
              <Loader2 size={14} className="animate-spin mr-2" />
              Valuating...
            </>
          ) : (
            <>
              <RefreshCw size={14} className="mr-2" />
              New Valuation
            </>
          )}
        </Button>
      </div>

      {history && history.valuation_count > 0 ? (
        <>
          {/* Trend Summary */}
          {history.valuation_count >= 2 && (
            <div className="p-4 mb-6 bg-[#0A0A0A] border border-[#1A1A1A]">
              <div className="flex items-center gap-3 mb-2">
                {getTrendIcon(history.value_trend)}
                <p className="text-white font-medium">
                  {history.value_trend === 'up' 
                    ? `Appreciating ${history.trend_percentage > 0 ? '+' : ''}${history.trend_percentage}% since last valuation`
                    : history.value_trend === 'down'
                    ? `Depreciating ${history.trend_percentage}% since last valuation`
                    : 'Stable, no significant change since last valuation'}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Based on {history.valuation_count} AI valuations. 
                {history.value_trend === 'up' && ' Market demand and comparable sales support continued appreciation.'}
                {history.value_trend === 'down' && ' Consider reviewing recent auction results for comparable assets.'}
                {history.value_trend === 'stable' && ' Consistent valuations indicate a steady market position.'}
              </p>
            </div>
          )}

          {/* Difference Alert with Accept Button */}
          {history.latest_ai_value && Math.abs(history.latest_ai_value - history.current_user_value) / history.current_user_value > 0.1 && (
            <div className="p-4 mb-6 bg-gold/10 border border-gold/30 rounded">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <AlertCircle size={18} className="text-gold shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white text-sm font-medium">Value Discrepancy Detected</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      The AI estimate differs from your listed value by more than 10%.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleAcceptValuation}
                  disabled={accepting}
                  className="btn-primary shrink-0"
                  data-testid="accept-valuation-btn"
                >
                  {accepting ? (
                    <>
                      <Loader2 size={14} className="animate-spin mr-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Check size={14} className="mr-2" />
                      Accept AI Value
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* History List */}
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              Valuation History ({history.valuation_count} total)
            </p>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {history.history.map((record, index) => (
                <div 
                  key={record.id}
                  className={`p-4 border ${index === 0 ? 'bg-[#111111] border-gold/30' : 'bg-[#0A0A0A] border-[#1A1A1A]'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-lg text-white font-serif">
                        {formatCurrency(record.estimated_value)}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded ${getConfidenceColor(record.confidence)}`}>
                        {record.confidence}
                      </span>
                      {index === 0 && (
                        <span className="px-2 py-0.5 text-xs bg-gold/20 text-gold rounded">
                          Latest
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Calendar size={12} />
                      {formatTimeAgo(record.created_at)}
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground mb-2">
                    Range: {formatCurrency(record.value_range_low)} - {formatCurrency(record.value_range_high)}
                  </p>
                  
                  {record.reasoning && (
                    <p className="text-sm text-gray-400 italic">
                      "{record.reasoning}"
                    </p>
                  )}
                  
                  {record.market_factors && record.market_factors.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {record.market_factors.slice(0, 3).map((factor, i) => (
                        <span key={i} className="px-2 py-0.5 text-[10px] bg-[#1A1A1A] text-muted-foreground rounded">
                          {factor}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-2 text-[10px] text-muted-foreground">
                    Source: {record.source === 'scheduled' ? 'Weekly Auto-Valuation' : 
                            record.source === 'on_add' ? 'Initial Valuation' : 'Manual Request'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-8">
          <BarChart3 size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-white text-lg mb-2">No Valuations Yet</h4>
          <p className="text-muted-foreground text-sm mb-6">
            Get an AI-powered market valuation for this asset
          </p>
          <Button
            onClick={handleRequestValuation}
            disabled={valuating}
            className="btn-primary"
          >
            {valuating ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Generating Valuation...
              </>
            ) : (
              <>
                <Sparkles size={14} className="mr-2" />
                Get AI Valuation
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
